import { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { Bug } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { BugReportModal } from './BugReportModal';
import { useAuth } from '@/contexts/AuthContext';
import {
  FLOATING_CONTROLS_CHANGED_EVENT,
  REPORT_RAIL_MARGIN_PX,
  clampRailX,
  parseRememberedRailX,
  placeOnRail,
  railObstaclesFor,
} from '@/lib/layout/floatingControlsReserve';

const STORAGE_KEY = 'report-issue-button-position';
/** Keyboard step along the rail (←/→ while the button is focused; Home/End jump to the ends). */
const KEY_STEP_PX = 32;

/**
 * Report an Issue — a floating button on a BOTTOM RAIL (OCT20C-F1).
 *
 * It used to be freely draggable anywhere on screen, defaulting to bottom-left; at the bottom of a page it covered the
 * page's last controls (field finding F1). Now:
 *   - its vertical place is fixed: `bottom-4` above the iOS safe-area inset (`mb-safe-bottom`), 44 px tall, in the
 *     band the app shell keeps clear (`data-floating-control` → `--floating-controls-reserve`, see
 *     src/lib/layout/floatingControlsReserve.ts), so it can never cover an operational action that has been scrolled
 *     into view;
 *   - it is still draggable — along the rail — and still remembers where (`{x}` in localStorage; a legacy `{x, y}`
 *     is read for its x). A remembered or dragged x is clamped to the current viewport on mount, after it renders,
 *     on every resize/rotation and whenever another floating control changes, so it can never be stranded off-screen
 *     nor hidden under the Help button that shares its row (placeOnRail moves it to the nearest free side);
 *   - on a phone it collapses to a 44×44 icon (the label stays in the accessible name), so it covers as little of the
 *     page as possible while content scrolls past it;
 *   - keyboard: it is a normal focusable button (Enter/Space opens the reporter); ←/→ move it, Home/End jump;
 *   - it yields (visibility) while a modal holds the page — including its own reporter dialog.
 */
export function ReportIssueButton() {
  const [isOpen, setIsOpen] = useState(false);
  const { activeShop } = useAuth();
  const [x, setX] = useState<number>(() => {
    let raw: string | null = null;
    try { raw = localStorage.getItem(STORAGE_KEY); } catch { /* storage unavailable */ }
    const remembered = parseRememberedRailX(raw);
    // Width unknown before the first render: assume the widest form; re-clamped once measured.
    return clampRailX(remembered ?? REPORT_RAIL_MARGIN_PX, window.innerWidth, 200);
  });
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef(0);
  const hasDragged = useRef(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // On the rail, inside the viewport, and never under another floating control sharing the row (Help).
  const clampNow = useCallback((value: number) => {
    const el = buttonRef.current;
    const w = el?.offsetWidth || 200;
    return placeOnRail(value, window.innerWidth, w, el ? railObstaclesFor(el) : []);
  }, []);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ x })); } catch { /* storage unavailable */ }
  }, [x]);

  // Once rendered (its real width is known), on every resize / rotation, and whenever another floating control
  // mounts or changes (e.g. Help appears after the tutorial state loads), keep it on the rail and clear of them.
  useLayoutEffect(() => {
    if (!activeShop) return;
    const fix = () => setX((cur) => clampNow(cur));
    fix();
    window.addEventListener('resize', fix);
    window.addEventListener('orientationchange', fix);
    window.addEventListener(FLOATING_CONTROLS_CHANGED_EVENT, fix);
    return () => {
      window.removeEventListener('resize', fix);
      window.removeEventListener('orientationchange', fix);
      window.removeEventListener(FLOATING_CONTROLS_CHANGED_EVENT, fix);
    };
  }, [activeShop, clampNow]);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    setIsDragging(true);
    hasDragged.current = false;
    dragStart.current = e.clientX - x;
    e.preventDefault();
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    setIsDragging(true);
    hasDragged.current = false;
    dragStart.current = touch.clientX - x;
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    let next: number | null = null;
    if (e.key === 'ArrowLeft') next = x - KEY_STEP_PX;
    else if (e.key === 'ArrowRight') next = x + KEY_STEP_PX;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = window.innerWidth;
    if (next === null) return;
    e.preventDefault();
    setX(clampNow(next));
  };

  useEffect(() => {
    if (!isDragging) return;
    const move = (clientX: number) => {
      const next = clampNow(clientX - dragStart.current);
      setX((cur) => {
        if (Math.abs(next - cur) >= 1) hasDragged.current = true;
        return next;
      });
    };
    const handleMouseMove = (e: MouseEvent) => move(e.clientX);
    const handleTouchMove = (e: TouchEvent) => {
      move(e.touches[0].clientX);
      e.preventDefault(); // Prevent scrolling while dragging
    };
    const end = () => setIsDragging(false);

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', end);
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', end);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', end);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', end);
    };
  }, [isDragging, clampNow]);

  // Only render once shop data is available
  if (!activeShop) {
    return null;
  }

  return (
    <>
      <Button
        ref={buttonRef}
        data-floating-control="report-issue"
        data-yield-to-modal=""
        data-testid="report-issue-button"
        aria-label="Report an Issue"
        title="Report an Issue (drag, or ←/→ when focused, to move it along the bottom edge)"
        onClick={() => {
          if (!hasDragged.current) {
            setIsOpen(true);
          }
        }}
        onKeyDown={handleKeyDown}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        className="fixed bottom-4 mb-safe-bottom z-50 h-11 min-w-11 gap-2 px-3 shadow-lg cursor-move touch-none sm:px-8"
        size="lg"
        style={{ left: `${x}px` }}
      >
        <Bug className="h-5 w-5" aria-hidden="true" />
        <span className="sr-only sm:not-sr-only">Report an Issue</span>
      </Button>
      <BugReportModal open={isOpen} onOpenChange={setIsOpen} />
    </>
  );
}
